import React, { useContext } from 'react';
import { AppWindow, Globe, Server, Database, Search, Plus, Download, RefreshCw } from 'lucide-react';
import { WebAppCard } from '../components/web-applications/WebAppCard';
import { WebAppFilters } from '../components/web-applications/WebAppFilters';
import { AssetsApi, Port, PaginatedPortList, AssetsPortsWebListRequest } from 'api-client';
import { TablePagination } from '../components/ui/TablePagination';
import { useTeams } from '../contexts/TeamsContext';
import { AuthContext } from '../contexts/AuthContext';
import { getApiConfiguration } from '../api/utils';
import ExportActionBtn from '@/components/ui/ExportActionBtn';
import { Button } from '@/components/ui/button';



const WebApplications = () => {
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedTech, setSelectedTech] = React.useState('all');
  const [selectedStatus, setSelectedStatus] = React.useState(0);
  const [ports, setPorts] = React.useState<Port[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedOrdering, setSelectedOrdering] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);
  const [paginationInfo, setPaginationInfo] = React.useState<{ count: number; next: string | null; previous: string | null }>(
    { count: 0, next: null, previous: null }
  );

  const fetchWebPorts = React.useCallback(async () => {
    if (!currentTeam?.slug || !token) return;

    setIsLoading(true);
    setError(null);

    try {
      const api = new AssetsApi(getApiConfiguration(token));
      const requestParams: AssetsPortsWebListRequest = {
        teamSlug: currentTeam.slug,
        page: currentPage,
        search: searchQuery,
        limit: 12
      }
      if (selectedStatus) {
        requestParams.statusCode = selectedStatus;
      }
      if (selectedOrdering) {
        requestParams.ordering = selectedOrdering;
      }
      const response = await api.assetsPortsWebList(requestParams);
      setPorts(response.results);
      setPaginationInfo({
        count: response.count,
        next: response.next || null,
        previous: response.previous || null
      });
    } catch (err) {
      console.error('Error fetching web ports:', err);
      setError('Failed to load web ports');
    } finally {
      setIsLoading(false);
    }
  }, [currentTeam?.slug, token, currentPage, searchQuery]);

  const fetchAllWebPorts = async (limit: number) => {
    if (!currentTeam?.slug || !token) return [];
    
    const api = new AssetsApi(getApiConfiguration(token));
    const response = await api.assetsPortsWebList({
      teamSlug: currentTeam.slug,
      search: searchQuery,
      ordering: selectedOrdering,
      limit
    });
    return response.results;
  };

  React.useEffect(() => {
    fetchWebPorts();
  }, [fetchWebPorts]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <AppWindow className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Web Applications</h1>
            <p className="text-sm text-gray-400">Monitor and secure your web applications</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {/* <Button
            variant="outline"
            onClick={fetchWebPorts}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </Button> */}
          <ExportActionBtn
            fetchAllData={() => fetchAllWebPorts(paginationInfo.count)}
            filename="web_applications"
            disabled={ports?.length === 0 || isLoading}
          />
          <Button className="flex items-center gap-2" disabled>
            <Plus className="h-4 w-4" />
            <span>Add Application</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <WebAppFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedTech={selectedTech}
        onTechChange={setSelectedTech}
        selectedStatus={selectedStatus}
        onStatusChange={setSelectedStatus}
      />

      {/* Web Applications Grid */}
      {isLoading ? (
        <div className="py-12">
          <div className="flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
            <p className="text-gray-400">Loading web applications...</p>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-500/10 text-red-500 p-6 rounded-md border border-red-500/30 text-center">
          <p className="font-medium mb-2">Error Loading Data</p>
          <p className="text-sm">{error}</p>
          <Button 
            variant="outline" 
            className="mt-4 border-red-500/30 text-red-500 hover:bg-red-500/10"
            onClick={fetchWebPorts}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </div>
      ) : ports.length === 0 ? (
        <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-6 text-center">
          <AppWindow className="h-12 w-12 text-gray-500 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-white mb-2">No Web Applications Found</h3>
          <p className="text-gray-400 mb-4">{searchQuery ? 'No applications match your search criteria.' : 'No web applications have been detected yet.'}</p>
          {searchQuery && (
            <Button onClick={() => setSearchQuery('')}>
              Clear Filters
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ports.map((port) => (
            <WebAppCard key={port.id} app={port} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {!isLoading && ports.length > 0 && (
        <TablePagination
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          totalItems={paginationInfo.count}
          itemsPerPage={12}
          paginationInfo={paginationInfo}
          itemName="web applications"
        />
      )}
    </div>
  );
};
  
export default WebApplications;